// Three more of the task brief's literal fabrications, all pure text/DOM
// edits (no network — unlike ratingFixups.ts) so they share one
// MutationObserver-on-#app pass, the same pattern linkFixups.ts's own header
// explains at length (see there for why an observer and not a wrapped
// render()). Each fixup below no-ops when its own selector/text pattern
// isn't present — "not currently on that view" is a legitimate reason to do
// nothing, same as fixStoreBadges in linkFixups.ts.
//
// #2 — app.js:252, renderMosaic(): "9 markets · 14,546 listings". 14,546 is
// not a formula, it's Blake's own 9 fixture category counts summed by hand
// at authoring time (3184+2461+1726+942+2093+655+1108+887+1490 — verified by
// adding data.js's own CATEGORIES literals) and frozen into a string that
// never recomputes, even though our bridge already overwrites
// CATEGORIES[].count with the real per-category total (floorTransport.ts's
// bridgeScriptSource, the CATEGORIES hybrid loop). "9" is not fabricated the
// same way — it's RENDERABLE_CATEGORY_SLUGS.length, a real count of how many
// category tiles the mosaic renders, true regardless of how many listings
// exist in each. The task brief explicitly invites reconsidering whether the
// listings half is worth showing "at current scale" — with ~100 real
// listings across 9 categories today (floorTransport.ts's own
// MIN_LISTINGS_TO_INSTALL comment), a real total would read as "9 markets ·
// 104 listings" or worse, a two-digit number next to "markets" that makes the
// site look thin rather than credible, the exact bad trade the brief warns
// about. Decision: keep "N markets" (real, stable, always true), drop the
// listings clause entirely rather than replace it with an accurate but
// unflattering small number — dropping a claim nobody's making beats making
// a weak one, same principle as delabelLink in linkFixups.ts ("a label that
// is not a link is more honest than a link to the wrong page").
//
// #3 — app.js:432 and :453, renderCategoryPage()/renderBrowse(): literal
// "in the demo set" wording survives into a page real sellers' real listings
// render on. Text-only: the counts sitting next to that phrase (cat.count,
// items.length) are already real by the time this runs (the same
// CATEGORIES[].count / installed LISTINGS the mosaic reads) — only the
// wording is fabricated, not the number, so this only edits the phrase.
//
// #4 — app.js:610, renderDiscover(): the "Try" chips include "@miacurates",
// a deleted fixture persona's handle (floorTransport.ts's bridgeScriptSource:
// USERS is fully replaced, not added to — miacurates was one of Blake's ten
// reserved fixture handles and no longer exists). discSearchData() (app.js:491)
// matches listing titles/descriptions/categories and seller handles — a
// search for a handle that matches no real seller is a guaranteed-zero-result
// suggestion by construction, not a probabilistic one. This module has no
// live "what's actually trending" signal to substitute in this pass (DISC_TAGS
// is cleared, not wired — see floorTransport.ts's bridgeScriptSource header,
// and insightsFor()-adjacent work is explicitly out of scope), so per the
// brief's own fallback ("if not, removing beats suggesting a dead end") the
// chip is removed rather than replaced with an unverified guess.
//
// #5 — RETIRED 2026-08-24 when we took Blake v47. The fee-line override used
// to strip the "No listing fees during beta" clause at render time, per the
// owner call ("I am overriding blake here, delete that copy"). Blake removed
// the clause from his own source in v47, so the override would now be cutting
// text that is already cut.
//
// 🔑 THE RULING IS NOT DROPPED WITH THE CODE. It moved from a runtime override
// to a SOURCE GUARD — see demoTextFixups.test.ts, which asserts the clause is
// absent from public/web/app.js. A runtime fixup only holds while it runs; the
// guard fails the moment a future drop reintroduces the claim, which is the
// failure mode that actually matters. The claim is false: there is no
// listing-fee concept anywhere in the codebase, routes/listings.ts never
// touches Stripe on the create path, and help.tsx:119 already states the true
// version unqualified.

import { RENDERABLE_CATEGORY_SLUGS, clampAskPct, demoMoney, priceCheckAttributionLabel } from "./floorTransport";

const DEMO_SET_YET_RE = / in the demo set yet\./;
const DEMO_SET_SUFFIX_RE = / in the demo set$/;

/** #2. Deliberately takes no listings-count data — see the header comment
 *  above for why the listings clause is DROPPED rather than replaced with a
 *  real (but unflatteringly small) count.
 *
 * 🔴 F2 (adversarial review of #671, 2026-08-26): this used to hardcode
 * RENDERABLE_CATEGORY_SLUGS.length (a fixed 9) unconditionally — but
 * fixEmptyCategoryTiles (below) can remove up to all nine mosaic tiles when
 * a category has no listings, and "9 markets" over a 5-tile mosaic is
 * exactly the kind of literal-fabrication-that-outlives-its-premise this
 * whole module exists to retire. Measured live on staging the day this was
 * found: 5 of 9 categories populated, header still said "9 markets".
 *
 * Counts the SURVIVING `.mosaic .tile` elements instead — which is only
 * correct if fixEmptyCategoryTiles has already run this pass and removed the
 * empty ones (applyDemoTextFixups below calls it first for exactly this
 * reason). Falls back to RENDERABLE_CATEGORY_SLUGS.length only when the
 * mosaic itself is absent (a structural miss fixEmptyCategoryTiles already
 * warns about — not this fixup's job to warn twice about the same cause). */
function fixMarketsHeader(doc: Document): void {
  const el = doc.querySelector("#categories .sec-head .sec-link");
  if (!el) return; // not the home view this pass
  const mosaic = doc.querySelector("#categories .mosaic");
  const populated = mosaic ? mosaic.querySelectorAll(":scope > .tile").length : RENDERABLE_CATEGORY_SLUGS.length;
  const target = `${populated} markets`;
  if (el.textContent === target) return; // already fixed this pass
  if (!/\bmarkets\b/i.test(el.textContent ?? "")) {
    console.warn(
      `[web-install] demo-text fixup: "#categories .sec-link" did not match the expected "N markets · N listings" pattern (got "${el.textContent}")`,
    );
    return;
  }
  el.textContent = target;
}

/** #3a. The empty-category-page paragraph. */
function fixCategoryEmptyState(doc: Document): void {
  const el = Array.from(doc.querySelectorAll(".empty-note")).find((e) => DEMO_SET_YET_RE.test(e.textContent ?? ""));
  if (!el) return;
  el.textContent = (el.textContent ?? "").replace(DEMO_SET_YET_RE, " yet.");
}

/** #3b. The populated-category browse section's count line. */
function fixCategoryBrowseCount(doc: Document): void {
  const el = Array.from(doc.querySelectorAll("#browse .sec-count")).find((e) =>
    DEMO_SET_SUFFIX_RE.test(e.textContent ?? ""),
  );
  if (!el) return;
  el.textContent = (el.textContent ?? "").replace(DEMO_SET_SUFFIX_RE, " listed");
}

/** #4. Removing the chip is naturally idempotent — once gone, this selector
 *  finds nothing on every later pass. */
function fixDiscoverTryChip(doc: Document): void {
  const chip = doc.querySelector('.disc-try .quick-chip[data-dtry="@miacurates"]');
  chip?.remove();
}

/** U+2022. A mark, not a number — see fixUnreadBadge. */
const UNREAD_DOT = "•";

/**
 * #6 — the deals unread badge, added 2026-08-24 with the deals wiring.
 *
 * app.js:1810 renders the badge with the value interpolated RAW, not through
 * esc(), and only when truthy. Blake's fixture puts a COUNT there (unread: 2).
 *
 * 🔴 WE HAVE NO TRUTHFUL COUNT AND CANNOT GET ONE. `conversations` tracks a
 * per-participant read FLAG (participant1Read / participant2Read), not
 * per-message read state, and deal-threads.ts derives its boolean straight from
 * it. So a real unread count cannot be produced from the current schema at all,
 * and passing the boolean through paints the literal word "true" in the badge.
 *
 * The mapper coerces to 0/1 so the badge appears exactly when there IS unread
 * activity, and this replaces the numeral with a dot so it reads as a MARK
 * rather than as "one message". The other options were to print 1 and let
 * people read it as a count, or to add per-message read tracking — a migration,
 * and disproportionate for a badge. 🪤 The fourth, deriving a count from
 * "messages since my last message", is a plausible number with no source, which
 * is the fabrication class this lane exists to avoid.
 *
 * A GLYPH rather than a CSS-only dot, deliberately. CSS can round the pill but
 * cannot change textContent, so "true" would survive in the accessibility tree
 * and in copied text while looking correct on screen. It also sidesteps a
 * measured specificity trap: an equal-specificity rule in overrides.css LOSES
 * to a scoped one regardless of load order, which contradicts that file's own
 * header comment.
 */
function fixUnreadBadge(doc: Document): void {
  for (const el of Array.from(doc.querySelectorAll(".tr-unread"))) {
    // Idempotent, and it has to be: this runs on every mutation pass, and
    // writing textContent is itself a mutation that re-triggers the observer.
    if (el.textContent === UNREAD_DOT) continue;
    el.textContent = UNREAD_DOT;
  }
}

/**
 * #6 — app.js:748, the SIGNED-IN welcome header:
 *   `${THREADS.filter(t => t.act).length} deals need you · ${MARKET_VITALS.moved} in deals today`
 *
 * MARKET_VITALS is one of the thirteen globals the floor install CLEARS. It holds
 * Blake's hand-authored `{ dealsToday: 214, sentOffers: 481, moved: "$86K" }`, so
 * once cleared `.moved` is undefined and the header reads
 * **"undefined in deals today"**.
 *
 * 🔴 There is NO source for it. Grepped the whole api-server route surface for
 * dealsToday / sentOffers / market vitals / a stats-or-vitals route: nothing
 * exists. So this cannot be wired, only removed or invented.
 *
 * ⇒ The clause goes. Same ruling as fixMarketsHeader above, for the same reason:
 * a real platform-wide figure at today's scale would read worse than saying
 * nothing, and inventing one is the fabrication class this whole install refuses.
 * The FIRST half — "N deals need you" — is real (it counts THREADS we wired) and
 * stays.
 *
 * 🪤 Matched on the separator + trailing wording rather than on "undefined", so
 * it still fires if MARKET_VITALS is ever repopulated with a fabricated value
 * rather than left empty.
 */
function fixMarketVitals(doc: Document): void {
  for (const el of Array.from(doc.querySelectorAll<HTMLElement>(".welcome .sub, #welcome-head .sub"))) {
    const text = el.textContent ?? "";
    const cut = text.indexOf(" · ");
    /* 🪤 `\s*`, not `s*`. The original was a dropped backslash: `/…todays*$/`
       matches "in deals today" followed by zero-or-more literal LOWERCASE S,
       which happens to pass on today's exact string by luck and would fail on
       any trailing whitespace — the one thing the character class was there to
       absorb. It never fired incorrectly, so nothing observable changed; the
       regex simply was not testing what it says it tests. */
    if (cut === -1 || !/in deals today\s*$/.test(text)) {
      /* Loud on a non-match, matching fixMarketsHeader's own posture: this
         clause renders the literal "undefined in deals today" the moment
         MARKET_VITALS is cleared, so a silently-skipped fixup is a visible
         defect, not a quiet no-op. Only warns for elements that ARE the
         welcome sub-header and DO carry a separator — an element without one
         is a different line, not a drifted one. */
      if (cut !== -1) {
        console.warn(
          `[web-install] demo-text fixup: ".welcome .sub" did not match the expected ` +
            `"… · N in deals today" pattern (got "${text}")`,
        );
      }
      continue;
    }
    el.textContent = text.slice(0, cut);
  }
}

/**
 * #7 — app.js:1429-1430 (renderRepDash) and :2788-2789 (renderWallet), the level
 * progress bar:
 *
 *   prevMin = ptsNext - round((ptsNext - pts) / 0.35)
 *   pct     = (pts - prevMin) / (ptsNext - prevMin)
 *
 * With `pts === ptsNext` that is 0/0, and NaN survives both Math.max and
 * Math.min, so the bar renders `width: NaN%`.
 *
 * That happens on the NO-DATA path: mapSellerToUser emits honest zeros
 * (`pts: 0, ptsNext: 0`) and installWallet overwrites them for the signed-in
 * user — but installWallet is best-effort in a try/catch, so ONE failed fetch
 * leaves a real visitor's wallet and profile painting NaN. Measured, not
 * theorised (demoAuthedSurfaces.test.ts).
 *
 * 🔴 WHY REMOVE RATHER THAN SUPPLY A NUMBER. `ptsNext` is not just a bar width,
 * it is VISIBLE TEXT at app.js:1447 —
 * "0 / 1 pts · 1 to level 1" — so any value chosen here is a fabricated level
 * threshold on screen. The bar itself is a CONSTANT 65% by construction for every
 * non-zero input, so it carries no information worth preserving either.
 *
 * ⇒ Drop the whole progress block when it could only be meaningless.
 *
 * 📌 DELIBERATELY ALSO COVERS THE MAXED CASE. At level 60 nextLevelPoints is null
 * and ptsNext legitimately equals pts, which floorTransport's own notes ruled
 * should be "handed to Blake rather than papered over". That ruling was about not
 * MINTING a 61st threshold — which this does not do. Removing a NaN bar is not
 * inventing a level; the maxed user simply stops seeing a broken one.
 */
function fixRepProgress(doc: Document): void {
  /* One selector covers BOTH sites — renderRepDash (app.js:1446) and
     renderWallet (app.js:2913) use the same `.rep-bar > i` inside the same
     `.rep-progress` wrapper. Verified against his markup rather than assumed;
     an earlier draft also guessed at `.lvl-bar`, which does not exist.

     📌 NOT `.cpp-bar` (app.js:1116). That is the referral banked-vs-potential
     bar, a different number with a different source, and it is not part of this. */
  for (const bar of Array.from(doc.querySelectorAll<HTMLElement>(".rep-bar i"))) {
    if (!/NaN/.test(bar.getAttribute("style") ?? "")) continue;
    (bar.closest(".rep-progress") ?? bar).remove();
  }
}

/**
 * #8 — app.js:3092, renderFolList(): the Followers/Following modal invents a
 * social graph out of the real seller directory.
 *
 * WHAT IT DOES TODAY. The header count is REAL and always zero —
 * `u.followers` / `u.following`, which mapSellerToUser hardcodes to 0 because
 * the web layer has no source for either. The ROSTER underneath it is
 * `Object.values(USERS).filter(x => x.handle !== handle)`: every OTHER real
 * seller on the floor, sorted by trade count, each with a real avatar, a real
 * handle, a real tier and a real trade count, presented as this person's
 * followers. So the modal contradicts itself — "Followers (0)" over 60 named
 * accounts — and every name in it is a real person asserted to be in a
 * relationship that does not exist.
 *
 * GUEST-OPEN. `[data-follist]` is the one control in its neighbourhood with
 * NO requireAuth wrap in wire() (vs `[data-sendfollowers]`'s handler in the
 * same function, which has one), and renderPublicProfile emits it for
 * any handle. One click from any seller's profile, signed out.
 *
 * ⇒ The roster is replaced with mobile's own empty copy whenever the real
 * count is 0 — which is every profile, today and for as long as followers
 * have no web source. Nothing is invented and nothing is hidden: the count
 * was already true, and the list now agrees with it.
 *
 * 🔑 WHY A DOM FIXUP AND NOT A CLICK INTERCEPT. Two reasons, and the second
 * is the load-bearing one:
 *
 *  1. Suppressing his open would leave a dead control on a guest-reachable
 *     profile and throw away the parts that are real — the header's true
 *     "(0)", his modal shell, and the close/backdrop handlers wire() rebinds.
 *     The modal is not the lie; the roster inside it is.
 *
 *  2. `folList` stays non-null for as long as the modal is open, so
 *     renderFolList() RE-EMITS the fabricated roster on every one of Blake's
 *     165 rerenderKeepScroll()/render() calls — including ones triggered from
 *     inside the modal itself. A one-shot intercept at open time is undone by
 *     the very next repaint. This module's MutationObserver on #app (see
 *     installDemoTextFixups) fires again for each of those repaints and
 *     re-applies, which is exactly the guarantee linkFixups.ts's header
 *     documents. Observer callbacks are microtasks, so the replacement lands
 *     before the browser paints — the fabricated handles are never visible,
 *     not even for a frame.
 *
 * It joins THIS module rather than getting an observer of its own because it
 * is a synchronous DOM edit with no network call — the exact criterion this
 * file's header already uses to separate itself from ratingFixups.ts.
 *
 * 📌 COPY REUSED, NOT WRITTEN: mobile/app/follow-list.tsx:41 already answers
 * this exact question on the other client — 'No followers yet' /
 * 'Not following anyone yet'. Two clients, one empty state.
 */
const NO_FOLLOWERS_COPY = "No followers yet";
const NO_FOLLOWING_COPY = "Not following anyone yet";

function fixFollowerRoster(doc: Document): void {
  const scroll = doc.querySelector(".rv-scroll.follist");
  if (!scroll) return; // modal not open this pass — a legitimate absence
  if (!scroll.querySelector(".fol-row")) return; // already replaced, or genuinely empty

  /* WHICH LIST, read from his own header rather than from module state: the
     modal's <h3> is `@handle · Followers (N)` / `· Following (N)`
     (app.js:3105). `folList.kind` is not reachable from here — it is an
     app.js top-level `let`, not a window property — and the header is the
     same value rendered. */
  const heading = scroll.closest(".modal")?.querySelector(".rv-head h3")?.textContent ?? "";
  const match = /·\s*(Followers|Following)\s*\((\d+)\)/.exec(heading);
  if (!match) {
    console.warn(
      `[web-install] demo-text fixup: follower modal heading did not match the expected ` +
        `"@handle · Followers (N)" pattern (got "${heading}") — the fabricated roster is left in place ` +
        `rather than replaced against an unread count`,
    );
    return;
  }

  /* 🔴 GATED ON THE REAL COUNT, not on "always empty it". Today the count is
     always 0 because mapSellerToUser has no source, but the day a real
     follower count arrives this fixup must get out of the way rather than
     hide it — and until the ROSTER also has a real source, a non-zero count
     with a fabricated list is a state this cannot honestly repair, so it is
     left alone and said out loud. */
  if (match[2] !== "0") {
    console.warn(
      `[web-install] demo-text fixup: ${heading.trim()} reports a NON-ZERO count — the roster below it is ` +
        `still Object.values(USERS) (app.js:3100) and is now fabricating against real data. ` +
        `Wire the real roster; this fixup only covers the zero case.`,
    );
    return;
  }

  const empty = doc.createElement("div");
  // Blake's own "nothing more to show" row inside this same scroller
  // (app.js:3113, `.fol-more`) — reusing his class keeps the empty state in
  // his type scale instead of introducing an unstyled one.
  empty.className = "fol-more";
  empty.textContent = match[1] === "Followers" ? NO_FOLLOWERS_COPY : NO_FOLLOWING_COPY;
  scroll.replaceChildren(empty);
}

/**
 * #8b — card 1218200003007957 (web profile fidelity): the follower/following
 * MODAL AFFORDANCE on the PUBLIC profile survives fixFollowerRoster's own
 * guard the moment profileFixups.ts starts patching real, non-zero
 * followers/following counts in from GET /api/users/:id. fixFollowerRoster
 * above only ever covered the count===0 case — the only case that could
 * exist before tonight — and its own comment already named this exact day:
 * "the day a real follower count arrives this fixup must get out of the
 * way rather than hide it... a non-zero count with a fabricated list is a
 * state this cannot honestly repair, so it is left alone and said out
 * loud." That day is this PR. renderFolList's roster (app.js:3523,
 * `Object.values(USERS).filter(...)`) has no real source to replace it
 * with — wiring an actual followers/following LIST is a separate, larger
 * card — so the only honest move tonight is closing the door to it, not
 * opening it onto a lie with a real count now sitting above it.
 *
 * WHY THE TRIGGER, NOT THE MODAL. Same reasoning fixFollowerRoster already
 * used, one level up: `[data-follist]` is the one control in its
 * neighbourhood with no requireAuth wrap in wire() (vs `[data-sendfollowers]`'s
 * share-menu item in the same function, which IS wrapped), so it is
 * guest-reachable from any seller's profile in one click. Converting the two
 * stat-link BUTTONS into plain, non-interactive `.stat` cells removes the only path to
 * folList/renderFolList from this view — nothing to click means nothing
 * left to fabricate. The header's own "N followers"/"N following" text
 * (app.js:1481, patched by profileFixups.ts) is untouched by this function
 * and stays real.
 *
 * PUBLIC PROFILE ONLY, matched the same way ratingFixups.ts's own
 * findRatingsTile tells the two profile templates apart: `[data-follow]` is
 * renderPublicProfile's own marker and renderProfile (the signed-in
 * visitor's OWN page) never emits it. The signed-in profile's
 * Followers/Following tiles are out of scope for this card and are left
 * exactly as they are.
 *
 * A FRESH ELEMENT, not an attribute strip on the live button. wire() may
 * already have bound this button's click listener by the time this observer
 * callback runs — MutationObserver callbacks are microtasks queued after
 * the synchronous render()+wire() pair that
 * triggered them. Deleting the `data-follist` attribute off the SAME node
 * would leave that listener attached and the button still clickable (the
 * listener reads `el.dataset.follist` at click time, but removing the
 * attribute doesn't remove the listener). Replacing the whole node — same
 * technique fixEmptyCategoryTiles above uses to retire a control outright —
 * takes the listener out of the document along with it.
 *
 * COPIES THE ALREADY-RENDERED TEXT rather than reading `u.followers` off
 * USERS (unreachable here — global lexical scope, this file's header) or
 * re-deriving anything: whatever the `.n`/`.l` children currently say is
 * carried into the replacement cell verbatim, so this composes correctly
 * regardless of whether profileFixups.ts's count patch has already run this
 * pass or runs later in a separate observer callback — either order leaves
 * the real number standing.
 *
 * Idempotent by construction: the replacement is a plain `.stat` div with
 * no `data-follist` and no `.stat-link` class, so this selector finds
 * nothing on every later pass.
 */
/* 🔑 BOTH profile views, not just the public one.
 *
 * This gated on `[data-follow]`, which only renderPublicProfile emits, so
 * the door it exists to close stayed WIDE OPEN on the signed-in owner’s own
 * profile — where `renderFolList` builds its roster from
 * `Object.values(USERS)`, i.e. every other user on the floor, under its own
 * comment calling it a "demo roster".
 *
 * That mattered more once the counts became real. A tile reading 0 that
 * opens a fabricated list is bad; a tile reading a TRUE 12 that opens the
 * same fabricated list is worse, because the number lends it credibility. */
function neutralizePublicFollowerTriggers(doc: Document): void {
  const anchor =
    doc.querySelector(".profile.wrap [data-follow]") ??
    doc.querySelector(".profile.wrap #edit-profile");
  if (!anchor) return; // on neither profile view this pass
  const wrap = anchor.closest(".profile.wrap");
  const triggers = wrap ? Array.from(wrap.querySelectorAll(".stats-row.stats-7 .stat-link[data-follist]")) : [];

  /* 🔴 F2 (adversarial review, card 1218200003007957): the scoped selector
     above is the ONLY thing standing between a real, non-zero follower
     count (profileFixups.ts) and a still-open door to renderFolList's
     fabricated roster — see this function's own header. A silent
     zero-match here (app.js moving `.stat-link` off the button, renaming
     `.stats-row`/`.stats-7`, or restructuring the markup this selector
     depends on) would leave `[data-follist]` guest-reachable again with
     nothing anywhere saying so. Testing the UNSCOPED selector is what tells
     "the control is gone" (legitimately nothing to do) apart from "the
     control moved out from under this selector" (silently broken): if a
     `[data-follist]` element exists anywhere in `wrap` at all, the scoped
     query missing it is drift, not absence. */
  if (wrap && triggers.length === 0 && wrap.querySelector("[data-follist]")) {
    console.warn(
      '[web-install] demo-text fixup: ".stats-row.stats-7 .stat-link[data-follist]" matched nothing on a ' +
        "profile view even though a [data-follist] element exists — neutralizePublicFollowerTriggers may no longer be " +
        "closing the follower-roster trigger",
    );
  }

  for (const btn of triggers) {
    const nText = btn.querySelector(".n")?.textContent ?? "";
    // Strips the trailing " ›" statsGrid appends to every clickable cell's
    // label (app.js:1109) — the chevron implies "opens something", which is
    // no longer true once this cell is a plain, inert `.stat`.
    const labelText = (btn.querySelector(".l.microlabel")?.textContent ?? "").replace(/\s*›\s*$/, "");
    const cell = doc.createElement("div");
    cell.className = "stat";
    const n = doc.createElement("div");
    n.className = "n";
    n.textContent = nText;
    const l = doc.createElement("div");
    l.className = "l microlabel";
    l.textContent = labelText;
    cell.append(n, l);
    btn.replaceWith(cell);
  }
}

/**
 * #9 — app.js:883, the Price Check ask marker paints OUTSIDE the card.
 *
 *   <span class="ask" style="left:${Math.round(((l.declaredValue - pc.min) /
 *                                   (pc.max - pc.min)) * 76 + 12)}%">
 *
 * The formula assumed HIS band — ±15% around the seller's own ask, which
 * brackets the price by construction, so the dot always landed near 50%. Real
 * stored bands do not bracket anything: measured over the live floor, 12 of the
 * 53 auto-revealed rows compute a `left:` between -64% and 341%. `.pc-bar`
 * (styles.css:962) sets no overflow and nothing above it clips, so the dot
 * paints in unrelated page space — confirmed in Chromium, `elementFromPoint`
 * returns the dot. Guest-visible with no click, because the bridge reveals
 * every row that already has a stored answer.
 *
 * ⇒ The `left:` is clamped into [4, 96] — clampAskPct in floorTransport.ts,
 * whose bounds are `listing-og.ts`'s `priceCheckAskPct` to the digit. See there
 * for why an off-band ask is PINNED to the edge rather than hidden (the price
 * is stated, so "beyond this end of the band" is true and useful) and why an
 * uncomputable one is removed instead (the SSR page's own omit case).
 *
 * 🪤 THE VALUE IS READ OFF THE STYLE ATTRIBUTE, NOT OFF `el.style.left`.
 * `left:NaN%` is invalid CSS, so the CSSOM drops the declaration and
 * `el.style.left` returns "" — indistinguishable from a dot with no `left` at
 * all, which must be left alone rather than deleted. The raw attribute still
 * carries the literal `NaN%`, so that is what gets parsed.
 *
 * 🪤 `:not(.wz-pc)` — the LISTING panel only. app.js:1615's wizard twin is a
 * separate fabrication (its dot is hardcoded `left:50%`, so it needs no clamp)
 * and is being fixed in its own lane; editing its DOM from here would collide
 * with that change.
 *
 * Idempotent by construction: Blake already Math.round()s, so an in-range dot
 * parses back to exactly the value this would write and nothing is set. Only
 * an out-of-band dot is written, once, and its clamped value is in range.
 */
const ASK_LEFT_RE = /(?:^|;)\s*left\s*:\s*([^;]+)/i;

function fixPriceCheckAskMarker(doc: Document): void {
  for (const dot of Array.from(doc.querySelectorAll<HTMLElement>(".pricecheck:not(.wz-pc) .pc-bar .ask"))) {
    const declared = ASK_LEFT_RE.exec(dot.getAttribute("style") ?? "");
    if (!declared) continue; // no position asserted — nothing to repair
    const pct = clampAskPct(Number.parseFloat(declared[1]));
    if (pct === null) {
      // Not a number at all: no position exists to plot, so none is shown.
      dot.remove();
      continue;
    }
    if (Number.parseFloat(declared[1]) === pct) continue; // already where it belongs
    dot.style.left = `${pct}%`;
  }
}

/**
 * #10 — `grounded: false` renders as a confident market finding.
 *
 * `priceVerifier.ts` races a live market search against a timeout, and on a miss
 * the estimate proceeds UNGROUNDED: the model prices the item from memory. The
 * flag rides in the same JSON the panel's range comes from, and Blake's markup
 * has no site for it, so an estimate with no market data behind it painted as
 * "$90 – $140 · HIGH CONFIDENCE · PRICED FAIR" with nothing saying so.
 *
 * That exact disagreement between two surfaces is bug 74b5c619, and mobile
 * already owns the ruling — `mobile/lib/priceCheckAttribution.ts`, whose header
 * says the rule lives in one place because two surfaces disagreeing IS the bug.
 * priceCheckAttributionLabel in floorTransport.ts is that rule, mirrored and
 * pinned to mobile's file by a source guard in demoPriceCheckPanel.test.ts.
 *
 * 🔑 THE "NO RENDER SITE" OBJECTION IS DEAD. The earlier decision not to carry
 * these fields rested on Blake having no element for them. This module already
 * builds DOM he never wrote — fixFollowerRoster, forty lines up — so that was an
 * assumption, not a constraint.
 *
 * 📌 textContent, and marketplace NAMES as plain text: never an href, never
 * innerHTML. `sources` is model output; mobile renders it in a <Text> too
 * (listing/[id].tsx:702). Reuses his own `.pc-reason` class so the line lands in
 * his muted type scale, same trick as fixFollowerRoster's `.fol-more`.
 *
 * 🪤 WHICH LISTING, from the hash. His reveal panel carries no id (only the
 * PRE-run button has `data-runpc`), and the detail view is the one place it
 * renders, so `#/listing/<id>` — app.js's own route() pattern — is the id. A
 * hash that does not match means the panel is not a listing's, and nothing is
 * appended.
 */
const PC_ATTRIBUTION_CLASS = "gbz-pc-attribution";
const LISTING_HASH_RE = /^#\/listing\/(.+)$/;

/** The bridge's read-only lookup into LISTINGS (floorTransport.ts's
 *  bridgeScriptSource). Typed structurally so this module does not import the
 *  full DemoPriceCheck just to read two optional fields. */
type PriceCheckLookup = (id: string) => { grounded?: boolean; sources?: string[] } | null;

function fixPriceCheckAttribution(doc: Document, win: Window): void {
  const lookup = (win as Window & { __gbzListingPriceCheck?: PriceCheckLookup }).__gbzListingPriceCheck;
  // Absent only if the bridge drifted; installDemoTextFixups says so once, at
  // install, rather than once per mutation pass.
  if (typeof lookup !== "function") return;

  const match = LISTING_HASH_RE.exec(win.location.hash);
  for (const panel of Array.from(doc.querySelectorAll(".pricecheck.pc-reveal:not(.wz-pc)"))) {
    const existing = panel.querySelector(`.${PC_ATTRIBUTION_CLASS}`);
    const label = match ? priceCheckAttributionLabel(lookup(match[1])) : null;
    if (label === null) {
      existing?.remove();
      continue;
    }
    if (existing) {
      // Idempotent: writing is itself a mutation, and this runs on every pass.
      if (existing.textContent !== label) existing.textContent = label;
      continue;
    }
    const line = doc.createElement("p");
    line.className = `pc-reason ${PC_ATTRIBUTION_CLASS}`;
    line.textContent = label;
    panel.appendChild(line);
  }
}

/**
 * #11 — app.js:45 (renderNav, mega-menu), :257 (renderMosaic, homepage), :638
 * (renderDiscover, "Browse by Vibe"): all three paint
 * `<img src="${c.tile}">` for every category, including ones with zero real
 * listings.
 *
 * floorTransport.ts's CATEGORIES hybrid loop (bridgeScriptSource) sets
 * `CATEGORIES[c].tile = tiles[slug] || ""` for exactly that case — an empty
 * categoryTiles miss, on purpose (see that file's own comment: a real
 * listing's image or nothing, never Blake's undelivered demo assets or a
 * fabricated path). MEASURED in Chromium: an empty `src` on `.tile`/
 * `.mega-tile` (neither sets a CSS background) paints the small broken-image
 * glyph plus alt text; `.disc-cattile` (which DOES set `background:
 * var(--band)`) paints a clean color card with no glyph — but the ruling
 * below applies uniformly regardless of which of the three looks broken on
 * its own.
 *
 * NICK'S RULING (owner call, 2026-08-26, mirrors the mobile app): a category
 * with zero listings gets NO TILE AT ALL, not a placeholder, not a broken
 * image. So the whole tile control is removed, not just its image.
 *
 * WHY REMOVE RATHER THAN HIDE. Every other structural fixup in this module
 * that retires a whole control (fixDiscoverTryChip) removes it outright — a
 * removed element can't regress into a differently-broken visible state the
 * way a `display:none` class could if some other rule ever outranked it, and
 * a removed grid item is excluded from grid layout exactly like a
 * `display:none` one, so nothing is lost by preferring the simpler
 * mechanism. Idempotent by construction, same as fixDiscoverTryChip: once
 * removed, the selector finds nothing on every later pass.
 *
 * `.disc-cattile` in practice never needs this: renderDiscover's own
 * `CATEGORIES.filter((c) => catCount(c.slug) > 0)` (app.js:636) already
 * excludes zero-listing categories before this fixup ever runs — `catCount`
 * (a live LISTINGS scan) and `categoryTiles` (floorTransport takes the first
 * listing's image per category) agree in every case today's API can produce.
 * Included anyway as defense in depth: nothing stops a future listing that
 * legitimately has no image field from being the only one in its category,
 * which would set count > 0 with tile still "".
 *
 * THE MOSAIC'S GRID SLOT. Unlike the other two, `.mosaic` (styles.css:431-
 * 443) is a FIXED named-area grid — nine hand-placed `grid-area` rects, one
 * per category slug — so removing a tile leaves its named area an empty
 * rectangle; a neighbor cannot flow into a cell it isn't assigned to.
 * `.mega-grid`'s `repeat(9, 1fr)` (styles.css:256) has no named areas — a
 * shorter tile count auto-places left-to-right with nothing pinning a hole in
 * the middle, but at a fixed 9-column width it leaves a TRAILING gap on the
 * right instead. `.disc-cats` needs neither: `auto-fill` there has no fixed
 * column COUNT to strand, and per the paragraph above the fixup essentially
 * never fires on it.
 *
 * 🔴 F1/L1 (adversarial review of #671, 2026-08-26, then re-review): TWO
 * PRIOR FIXES for both grids above, both measured broken:
 *
 *   1. `grid-template-columns: repeat(auto-fit, minmax(Npx, 1fr))`. Only
 *      collapses a track empty in EVERY row — once survivors exceeded
 *      however many columns fit at a width, the grid wrapped and the
 *      unfilled part of that row stayed reserved. Measured live on staging:
 *      1024px → 717.7px blank (75.9% of the last row); 768px → 233.6px;
 *      534px → 247.5px.
 *
 *   2. `repeat(var(--gbz-tile-cols), 1fr)` — a custom property pinning
 *      column count 1:1 to survivor count, so there is only ever one row
 *      (no wrap, so no partial-row blank). This genuinely fixed the blank
 *      strip (verified: 0px gap across 1-8 survivors × 6 breakpoints), but
 *      traded it for a DIFFERENT defect the same re-review caught: at high
 *      survivor counts on a phone, forcing N columns into a ~340px viewport
 *      makes each tile a sliver. Measured: 8 survivors @375px = 31.9×165px
 *      tiles, 82.6px of a 114px label clipped — starting at just 3
 *      survivors on a phone, and staging's real 5-survivor case clips
 *      56.3px TODAY. A column CAP was considered and rejected: capping
 *      columns below survivor count reintroduces wrapping, which
 *      reintroduces the exact partial-last-row blank strip #1 above
 *      already proved is the wrong failure mode.
 *
 * FIX: `display:flex; flex-wrap:wrap` on the container, `flex: 1 1 <basis>`
 * on each tile (overrides.css). Flexbox's own `flex-grow` stretches
 * whichever tiles land in the final row to fill it exactly — unlike a grid
 * track, which stays whatever width the template says regardless of what's
 * actually in it — while the flex-basis floor keeps every tile above a sane
 * minimum width instead of dividing the row by however many survive.
 * Measured across the same 1-8 × 6-breakpoint matrix: 0px gap AND 0px label
 * clipping at every combination, minimum tile width never below 182px
 * desktop / 339px @375px. `display:flex` makes every `grid-*` property
 * inert, so overrides.css carries no grid declarations on these rules at
 * all — nothing left dead to confuse a future reader.
 *
 * 🪤 SEAM B (same re-review): count===0 is a real, if rare, state (every
 * category empty) that "short" (this function's own reflow gate) does NOT
 * cover — short means PARTIALLY populated, false at both 0 and 9. Left
 * alone, Blake's `.mosaic` keeps its explicit `grid-template-rows:
 * repeat(3, 165px)` (styles.css:435) whether or not it has children, so an
 * empty mosaic still paints its own ~519px blank box under a "0 markets"
 * heading. Handled below by hiding `#categories`/`#mega` outright rather
 * than adding a third CSS state to the two reflow rules above.
 *
 * 🪤 HARDENED PREDICATE (F3): reads `btn.firstElementChild` rather than
 * `btn.querySelector("img")`. In the real markup the tile's own `<img>` IS
 * always the first element child (verified against all three render sites),
 * so this is a strictly narrower match than "the first img anywhere inside
 * the button" — a decoy `<img>` inserted as a descendant of the label/
 * overlay `<span>` (or between the button's open tag and the real img) no
 * longer gets mistaken for the tile's own image.
 */
const MOSAIC_REFLOW_CLASS = "gbz-mosaic-reflow";
const MEGA_REFLOW_CLASS = "gbz-mega-reflow";

function fixEmptyCategoryTiles(doc: Document): void {
  for (const btn of Array.from(doc.querySelectorAll(".tile, .mega-tile, .disc-cattile"))) {
    const img = btn.firstElementChild;
    if (!(img instanceof HTMLImageElement)) {
      // A tile button that doesn't start with an <img> at all is a
      // structural drift from the markup this fixup depends on (see the
      // hardened-predicate note above) — loud, not silent, same posture as
      // every other structural-miss warning in this module.
      console.warn(
        `[web-install] demo-text fixup: a "${btn.className}" tile's first child is not an <img> — fixEmptyCategoryTiles cannot tell whether it needs hiding`,
      );
      continue;
    }
    if (img.getAttribute("src") === "") btn.remove();
  }

  const categories = doc.getElementById("categories");
  const mosaic = categories?.querySelector<HTMLElement>(".mosaic") ?? null;
  if (categories && !mosaic) {
    console.warn(
      '[web-install] demo-text fixup: "#categories .mosaic" not found — fixEmptyCategoryTiles cannot reflow the homepage grid',
    );
  }
  if (mosaic) {
    const count = mosaic.querySelectorAll(":scope > .tile").length;
    const short = count > 0 && count < RENDERABLE_CATEGORY_SLUGS.length;
    mosaic.classList.toggle(MOSAIC_REFLOW_CLASS, short);
    /* SEAM B (adversarial re-review of #671, 2026-08-26): count===0 is a
       real, if rare, state — every category empty — and it used to leave
       Blake's named-area grid painting its own empty box under a "0
       markets" heading: .mosaic keeps grid-template-rows: repeat(3, 165px)
       (styles.css:435) whether or not it has children, an EXPLICIT track
       count that reserves its ~519px of height regardless of content. No
       reflow class fixes that, because none applies here (short is false at
       count 0, same as at count 9 — "short" means partially populated, not
       "has content"). Hides the whole section instead of leaving a
       heading over a blank box. Cleared (not just skipped) on a real count
       so a category coming back mid-session un-hides it — this branch runs
       on every pass, not just the first. */
    if (categories) categories.style.display = count === 0 ? "none" : "";
  }

  const mega = doc.getElementById("mega");
  const megaGrid = mega?.querySelector<HTMLElement>(".mega-grid") ?? null;
  if (mega && !megaGrid) {
    console.warn(
      '[web-install] demo-text fixup: "#mega .mega-grid" not found — fixEmptyCategoryTiles cannot reflow the mega menu',
    );
  }
  if (megaGrid) {
    const count = megaGrid.querySelectorAll(":scope > .mega-tile").length;
    const short = count > 0 && count < RENDERABLE_CATEGORY_SLUGS.length;
    megaGrid.classList.toggle(MEGA_REFLOW_CLASS, short);
    // Seam B's mega equivalent — same reasoning as the #categories branch
    // above, hiding #mega itself rather than leaving an empty dropdown a
    // click could still open. Inline display:none outranks Blake's own
    // .mega/.mega.open class toggle (styles.css:253-255) by specificity, so
    // this also makes the (now-pointless) open control inert; clearing it
    // back to "" on a real count hands control back to his own toggle.
    if (mega) mega.style.display = count === 0 ? "none" : "";
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   THE WALLET FIXUPS (#12–#14, 2026-08-26)

   Three money claims on ONE screen that Blake's own template cannot make
   correctly, because the facts they need are not in it. All three read the
   bridge's __gbzWalletFacts() — the same shape fixPriceCheckAttribution uses
   for __gbzListingPriceCheck, and for the same reason: WALLET is a data.js
   const in a classic script's lexical scope, and this is an ES module.

   🔑 WHAT IS DELIBERATELY *NOT* FIXED HERE — the hero badge.
   renderWallet opens `const held = walletEscrowTotal()`, which is data.js's
   own `WALLET.escrowHeld.reduce((s, t) => s + t.amount, 0)`, and paints it as
   "$X held in escrow". That looks like the classic recompute-instead-of-read
   defect and IS NOT one: the array it sums is exactly the list of cards
   rendered directly beneath it in "Active Escrow", so badge and list tie out
   by construction — the same invariant the wallet ROUTE enforces on purpose
   (users.ts: "the badge always equals the sum of the displayed ... line
   items — the previous bug summed ALL held but only listed the 20 most
   recent"). Swapping in the server's pendingEscrowTotal would reintroduce
   that bug from the other side: that figure is SELLER-ONLY by design, so a
   viewer who is the BUYER in a live escrow would see a badge smaller than
   the cards under it, and at zero would see no badge over a populated list.
   The badge is left alone and the canonical figure is surfaced ADDITIVELY,
   where it is unambiguously about payouts — see fixSellerPendingEscrow.
   ══════════════════════════════════════════════════════════════════════════ */

/** The bridge's read-only wallet lookup (floorTransport.ts's
 *  bridgeScriptSource). Structural, like PriceCheckLookup above, so this module
 *  does not import DemoWallet to read four fields. */
interface WalletFacts {
  /** Has a wallet response landed at all? Stated by the bridge, never inferred
   *  from the values — every one of them is 0/false at boot too. */
  installed: boolean;
  pendingEscrowTotal: number;
  payoutsLoaded: boolean;
  promoCreditBalance: number;
}
type WalletFactsLookup = () => WalletFacts;

const PENDING_ESCROW_CLASS = "gbz-pending-escrow";

/* ────────────────────────────────────────────────────────────────────────────
   ⚠️ BLAKE OWNS COPY — PLACEHOLDERS, PENDING HIS WORDING
   ────────────────────────────────────────────────────────────────────────────
   The two strings in this block are the only ones on these surfaces that this
   lane WROTE rather than sourced. They exist because the states they describe
   have no wording yet anywhere on the platform — not in app.js, not in mobile.
   They are placeholders that say the true thing plainly, and they are grouped
   here so replacing them is one edit against one list rather than a hunt.

   Everything else painted by the wallet fixups is an EXISTING string, quoted
   from Blake's own template or from mobile — see ESCROW_SELLER_NOTE below.
   ──────────────────────────────────────────────────────────────────────────── */

/** PLACEHOLDER. The honest replacement for "Connect bank to receive payments"
 *  when GET /api/stripe/payouts did not answer. No existing string covers "we
 *  could not read your payout state" — the platform has no wording for a
 *  failed read on this surface at all, which is why the failure was rendering
 *  as a false fact about the visitor's account instead. */
const PAYOUTS_UNAVAILABLE_COPY = "Payout details couldn't be loaded";

/** PLACEHOLDER. Labels the seller's canonical pending proceeds in the payout
 *  section.
 *
 *  🔴 IT EXISTS TO BREAK A COLLISION, not for flavour. This line first read
 *  "$312.50 held in escrow" — the IDENTICAL phrase Blake's hero badge uses for
 *  a DIFFERENT number ("$1,112.50 held in escrow", the both-roles sum of the
 *  cards below it). One screen, one phrase, two figures: a reader comparing
 *  them concludes one of them is wrong, and they would be right to.
 *
 *  Built from vocabulary that already exists rather than invented whole:
 *  mobile badges exactly this state PENDING on its own per-sale rows (the
 *  PayoutBadge map in app/wallet.tsx), inside sections both platforms already
 *  call "Seller Payout Account" and "Payout History". "held in escrow" is left
 *  to the hero, which is the badge that owns it. */
const PENDING_PAYOUT_LABEL = "Pending payout";

/** Blake's own seller-side wait note (app.js, the Active Escrow card), reused
 *  verbatim rather than reworded: it is already the sentence this platform uses
 *  for "your money, not yours yet". NOT a placeholder — his words. */
const ESCROW_SELLER_NOTE = "Buyer must confirm receipt before funds are released to your account.";

function walletFacts(win: Window): WalletFacts | null {
  const lookup = (win as Window & { __gbzWalletFacts?: WalletFactsLookup }).__gbzWalletFacts;
  if (typeof lookup !== "function") return null;
  return lookup();
}

/** The credit row's titles as its <b> reads them (textContent, so "&", not
 *  app.js's "&amp;"). See fixFoundingMemberCredit. */
const PROMO_CREDIT_ROW_TITLES = new Set(["Founding-member credit", "List & Earn credit"]);

/** What can still be in "Credits & Discounts" once the empty credit row is
 *  gone: coupon rows, and List & Earn's offer card and in-progress row. */
const SECTION_CONTENT = ".wal-row, .promo-offer, .promo-live-row";

/**
 * #12 — the permanent "$0.00 · Redeemed" founding-member credit row.
 *
 * app.js's "Credits & Discounts" section calls
 * `walRow(STAR_SVG, "gold", "Founding-member credit", ...)` with NO guard —
 * or, since 2026-10-05, the same row titled "List &amp; Earn credit" when the
 * credit it shows is a List & Earn one (card 1219177863701651) —
 * the one row on that screen that isn't gated, while every sibling section
 * (cards, coupons, payouts, escrowHeld, escrowPast) checks `.length` first.
 * With no promo credit, mapWallet feeds it honest empties and his template
 * paints a real-looking gold perk row reading "Redeemed  · applies to your
 * fees / $0.00" — a credit line for money that does not exist.
 *
 * 🔑 THE SERVER ALREADY RULED ON THIS, and the current wiring contradicts it.
 * GET /api/users/me/promo-credit's own header states the product spec:
 * "balanceCents 0 ⇒ the client renders NOTHING — no zero-value line, no empty
 * state (plan-founding-member-credit.md, slice 2)." So this is not a taste
 * call about empty states; it is the client obeying a written contract it was
 * violating.
 *
 * Removed, not hidden: the row carries no id or handler, so there is nothing
 * to keep around, and removal is naturally idempotent — once gone the selector
 * finds nothing on every later pass, same as fixDiscoverTryChip.
 *
 * Matched STRUCTURALLY on the row's own title text, never on position: it is
 * the first `.wal-row` in that section today and would silently become the
 * wrong row the moment a card or coupon is added above it. Either of its two
 * titles is this row; any other title is not.
 */
function fixFoundingMemberCredit(doc: Document, facts: WalletFacts): void {
  /* 🔑 `installed` FIRST, like both siblings below. Every value on WalletFacts
     is 0/false at boot — __gbzInstallFloor writes a fully-shaped zeroed WALLET
     — so before a wallet response lands, "balance is 0" is not a reading, it is
     the absence of one. Removing the row on that basis asserts "you have no
     credit" before anyone has looked.

     The cost is honest and small: a visitor with no credit sees Blake's row for
     the length of one boot fetch. The alternative is a fixup that makes a claim
     from a default, which is the same class of error as the payouts empty state
     two functions down — and this flag exists precisely to tell those apart. */
  if (!facts.installed) return;
  if (facts.promoCreditBalance !== 0) return; // real credit — his row is correct
  for (const row of Array.from(doc.querySelectorAll<HTMLElement>(".wallet .wal-sec .wal-row"))) {
    if (!PROMO_CREDIT_ROW_TITLES.has(row.querySelector("b")?.textContent?.trim() ?? "")) continue;
    const section = row.closest<HTMLElement>(".wal-sec");
    row.remove();
    /* Seam, and the same one fixEmptyCategoryTiles already rules on: this row
       is the ONLY unconditional child of "Credits & Discounts", so removing it
       from a wallet with no coupons either leaves a heading and a
       "Applied automatically at checkout" note over an empty box. Hidden
       rather than removed, and re-cleared on every pass, so a coupon arriving
       mid-session brings the section back — his own click handlers push into
       WALLET.coupons and re-render.
       🔴 2026-10-08: List & Earn's offer card ("Start the 5 days", .promo-offer)
       and its in-progress row (.promo-live-row) live in this section too and
       are not .wal-rows, so counting only .wal-row hid the opt-in from every
       eligible person with no credit yet. Anything still in the box keeps it. */
    if (section) section.style.display = section.querySelector(SECTION_CONTENT) ? "" : "none";
  }
}

/**
 * #13 — the canonical seller payout figure, which web never surfaced at all.
 *
 * GET /api/users/me/wallet returns `pendingEscrowTotal`: the seller's NET
 * proceeds on held offers, computed to match GET /api/stripe/payouts'
 * `pendingEscrowCents` exactly, by that route's own statement of intent. Until
 * now `mapWallet` did not even declare the field, so the number that answers
 * "how much of my money is on its way to my bank" existed on the wire and
 * appeared on no web surface.
 *
 * 🪤 IT DOES NOT GO IN THE HERO. See this block's header: the hero badge is
 * the sum of the cards below it and must stay that. This line goes in "Seller
 * Payout Account", where every other figure is already seller-side and about
 * the bank — so the two numbers cannot be read as competing answers to one
 * question. Mobile puts the same figure over its own "available to withdraw"
 * line, which is the same placement decision.
 *
 * 🔴 IT MUST NOT REUSE THE HERO'S PHRASE. This line originally read
 * "$312.50 held in escrow", which is word-for-word what the hero badge says
 * over a DIFFERENT number — the both-roles sum of the cards below it. The two
 * figures are both correct and both correctly labelled only if the labels
 * differ; identical wording over two figures on one screen reads as an
 * arithmetic error in the product. See PENDING_PAYOUT_LABEL for where the
 * replacement vocabulary comes from. The explanatory half is still Blake's own
 * sentence, verbatim.
 *
 * Gated on > 0, mirroring both his own `held > 0 ?` hero gate and mobile's
 * `apiPendingEscrowTotal > 0 ?`: a seller with nothing in escrow gets no line
 * rather than a "$0.00" one — the same ruling as #12 one section above.
 */
function fixSellerPendingEscrow(doc: Document, facts: WalletFacts): void {
  const section = Array.from(doc.querySelectorAll(".wallet .wal-sec")).find(
    (el) => el.querySelector(".sec-title")?.textContent?.trim() === "Seller Payout Account",
  );
  if (!section) return; // not the wallet view this pass
  const existing = section.querySelector(`.${PENDING_ESCROW_CLASS}`);
  if (!facts.installed || facts.pendingEscrowTotal <= 0) {
    existing?.remove();
    return;
  }
  const text = `${PENDING_PAYOUT_LABEL} ${demoMoney(facts.pendingEscrowTotal)} · ${ESCROW_SELLER_NOTE}`;
  if (existing) {
    // Idempotent: writing is itself a mutation and this runs on every pass.
    if (existing.textContent !== text) existing.textContent = text;
    return;
  }
  const line = doc.createElement("div");
  // His own amber escrow-info treatment, the one he already uses for the
  // "funds are locked" note — so this reads as part of the screen, not as an
  // announcement bolted onto it.
  line.className = `wal-info amber ${PENDING_ESCROW_CLASS}`;
  line.textContent = text;
  section.appendChild(line);
}

/**
 * #14 — "Connect bank to receive payments" is also what a FAILED fetch says.
 *
 * `mapWallet` reads `payouts?.payoutsEnabled ?? false` and
 * `payouts?.availableCents ?? 0`. `payouts` is null when GET /api/stripe/payouts
 * failed — installWallet treats all three optional calls as best-effort — so a
 * Stripe outage and a seller who genuinely never connected a bank produce the
 * identical screen. The visitor is told a fact about their account that we did
 * not establish, and the offered fix (connect a bank) is one they may already
 * have done.
 *
 * `installed && !payoutsLoaded` is the whole condition, and both halves are
 * load-bearing: at boot nothing has been attempted, and "we could not load it"
 * would be just as false a claim in the other direction.
 *
 * Text-only, and only on the WALLET hero. The same sentence appears in
 * renderAccount's quick-access row, which has the same conflation — left for
 * its own change rather than reached into from here, because that row's sub
 * carries the bank identity too and rewriting it is a different edit.
 */
function fixPayoutsUnavailable(doc: Document, facts: WalletFacts): void {
  if (!facts.installed || facts.payoutsLoaded) return;
  const el = doc.querySelector(".wallet .wal-hero .wal-avail.dim");
  if (!el) return; // bank connected, or not the wallet view
  if (el.textContent === PAYOUTS_UNAVAILABLE_COPY) return; // already fixed this pass
  el.textContent = PAYOUTS_UNAVAILABLE_COPY;
}

/**
 * #15 — a "Member since" label with nothing after it.
 *
 * Three of Blake's templates render the join date off a USERS entry and none of
 * them gate the label:
 *
 *   renderProfile        `<p class="sub">Member since ${u.memberSince}</p>`
 *   renderPublicProfile  `…Member since ${u.memberSince} · ${u.trades} trades…`
 *   renderTopCollections `<span class="cc-since microlabel">Member Since
 *                         ${esc(t.u.memberSince.split(" ").pop())}</span>`
 *
 * Both PRODUCERS of that field now carry the real date (mapSellerToUser from
 * GET /api/listings' seller select, mapDealCounterparts from the deal-threads
 * counterpart), so this is the residual rather than the fix: a response that
 * predates either column, an unparseable value, or a fourth producer added
 * later. The invariant is worth holding independently of who fills the field,
 * because the failure is silent and reaches a guest rail — topCollectors()
 * applies no minimum-activity threshold, so a brand-new counterpart can occupy
 * a Top Collections slot.
 *
 * 🪤 THE THIRD SITE NEEDS SURGERY, NOT REMOVAL. renderPublicProfile puts the
 * trades and follower counts in the SAME paragraph, so dropping the element
 * would take two real numbers with it; only the label and its separator go.
 * The other two carry nothing but the date and are removed whole.
 *
 * Idempotent by construction: every branch either removes the node or rewrites
 * its text to something this regex no longer matches.
 */
/** The LABEL only — never the separator that may follow it. An earlier draft
 *  swallowed a trailing "·" too, which made the shared-paragraph branch below
 *  unreachable: the separator was already gone by the time anything looked for
 *  it, so renderPublicProfile's line kept its dangling label. Caught by giving
 *  each of the three real render sites its own test. */
const MEMBER_SINCE_LABEL_RE = /^Member\s+[Ss]ince\b\s*/;

function fixDanglingMemberSince(doc: Document): void {
  /* Both `p.sub` sites live under `main.profile.wrap`, and the Top Collections
     card carries its own class. ONE pass over all three: what differs between
     them is not the selector but what is left once the label is removed. */
  for (const el of Array.from(doc.querySelectorAll<HTMLElement>(".cc-since, .profile .sub"))) {
    const text = (el.textContent ?? "").trim();
    if (!MEMBER_SINCE_LABEL_RE.test(text)) continue;
    const rest = text.replace(MEMBER_SINCE_LABEL_RE, "").trim();

    // A real date follows — the overwhelmingly common case, and nothing to do.
    if (rest.length > 0 && !rest.startsWith("·")) continue;

    if (rest.length === 0) {
      // renderProfile's line and the Top Collections card: label and date, and
      // nothing else. With no date the element holds only a dangling label.
      el.remove();
      continue;
    }

    /* renderPublicProfile, where the label shares a paragraph with two REAL
       numbers ("Member since  · 12 trades · 24 followers"). Removing the
       element would take the trades and follower counts with it, so only the
       label and its now-orphaned separator go. Rewriting textContent is itself
       a mutation, and this runs on every observer pass — idempotent because
       the result no longer starts with the label. */
    el.textContent = rest.replace(/^·\s*/, "");
  }
}


/**
 * app.js:869-872, renderDetail(): the FAIR/OVER split bars divide by a total
 * that is ZERO on every listing nobody has voted on yet.
 *
 * WHAT IT DOES TODAY. Both rows compute
 * `Math.round((fair / (fair + over)) * 100)`. With no votes that is 0/0 → NaN,
 * so the page paints a literal `NaN%` beside each label and the bar carries
 * `style="width:NaN%"`. Found on prod by the post-promotion guest smoke
 * (card 1217891492403239, Scotty Cameron listing) — and it is EVERY listing
 * until its first vote, guest-visible, on the detail view.
 *
 * WHY REMOVE THE BLOCK RATHER THAN PAINT 0%.
 *  · Mobile is the precedent and renders NO percentage at zero votes:
 *    PriceVoteBar.tsx gates the fill on `total > 0` and labels the state
 *    "No Votes". Painting `0%` on BOTH rows would assert a computed split
 *    where nothing was computed — a different falsehood from the one being
 *    fixed, and on the same surface.
 *  · 0% is a LEGITIMATE value here: fair 0 / over 5 renders "0%" correctly and
 *    must survive. ONLY the 0/0 case is broken and only 0/0 yields NaN, so the
 *    NaN is the precise discriminator. A total-based rule would have to
 *    re-derive the counts and could misfire on the legitimate zero.
 *  · It invents no copy — and copy is Blake's, not ours. His own `.d-votenote`
 *    directly above already reads "0 price votes so far", so the absence stays
 *    explained IN HIS WORDS. That line is pinned as an anchor precisely
 *    because this fix's honesty depends on it surviving.
 *
 * Same shape as fixRepProgress above: a NaN in the style attribute is the
 * signal, and dropping a meaningless bar is not inventing a number.
 */
function fixVoteBarsNoVotes(doc: Document): void {
  /* Scoped to `.vote-bars .vb-bar i` — NOT a bare `.vb-bar i`. Verified against
     his markup: `vote-bars` occurs exactly once in app.js (renderDetail:869),
     and `collectionOnly` listings never render the block at all.

     Removing the wrapper on the first hit detaches the second row, whose
     `closest()` then resolves to the already-detached wrapper; `.remove()` on a
     removed node is a no-op, so the loop stays safe and idempotent across the
     MutationObserver's repeated passes. */
  for (const bar of Array.from(doc.querySelectorAll<HTMLElement>(".vote-bars .vb-bar i"))) {
    if (!/NaN/.test(bar.getAttribute("style") ?? "")) continue;
    (bar.closest(".vote-bars") ?? bar).remove();
  }
}


/**
 * data.js:997 (the header brand mark) and app.js:3601 (the auth-wall modal
 * emblem) both emit `src="assets/logo-day.svg"` in day mode — the URL that
 * served POISONED content (the SPA shell as text/html) for ~26h across
 * 2026-08-26→27, with `Cache-Control: private, max-age=31536000`. The CDN was
 * purged the morning of the 27th, but every BROWSER that loaded a day-mode
 * page during the window stored the broken response for up to a YEAR and
 * repaints a broken brand mark from local cache without ever re-asking the
 * network (card 1217915730504222, Nick's last pre-vacation report).
 *
 * The only recovery that reaches those browsers is a DIFFERENT URL. Blake's
 * files are byte-sacred, so the version query is appended here at render
 * time: `assets/logo-day.svg?v=2`. Same file at the origin (the artifact
 * serves it query-blind), different cache key in every browser.
 *
 * 📌 DAY ONLY, deliberately. logo-night.svg was never poisoned (verified
 * serving image/svg+xml through the whole window), and busting it would
 * throw away a year of good caches for nothing. The selector is an EXACT
 * attribute match, so night mode's src never qualifies.
 *
 * Idempotent by construction: once swapped, the src no longer matches the
 * selector, so the MutationObserver's repeated passes skip it.
 */
const LOGO_DAY_BUST = "assets/logo-day.svg?v=2";

function fixLogoDayCacheBust(doc: Document): void {
  for (const img of Array.from(
    doc.querySelectorAll<HTMLImageElement>('img[src="assets/logo-day.svg"]'),
  )) {
    img.setAttribute("src", LOGO_DAY_BUST);
  }
}

/**
 * #18 — a "Top Collection" with nothing in it.
 *
 * `topCollectors()` (data.js:490) ranks EVERY key in USERS with no
 * minimum-activity threshold, and `renderTopCollections` (app.js:2027) only
 * declines to paint the section when fewer than three exist in total. So a user
 * with no listings on the floor occupies a card under a heading that reads "the
 * best collections on the floor · straight from the people who built them", and
 * on `#/collections` (guest-open) under "every collection on the floor · ranked
 * by the community".
 *
 * The CARD itself is honest — `0 Trades / 0 Sales / 0 Items / $0` and an empty
 * highlights strip. The false statement is the heading above it, which is why
 * this removes the card rather than rewriting any number inside it. We invent
 * no copy and contradict none.
 *
 * WHY NOW. mapCommentAuthors puts comment AUTHORS into USERS, which is what
 * makes a commenter's @handle resolve and their avatar real. Without this fixup
 * that same change would promote anyone who ever commented on a listing you
 * opened into "Top Collections" — trading a silent bounce for a false claim
 * about a real person, on a guest-facing rail. floorTransport's
 * mapDealCounterparts header already flags this exposure for counterparts
 * ("topCollectors() applies no minimum-activity threshold, so a brand-new
 * counterpart can occupy a Top Collections slot"); closing it here closes it for
 * them too, so the rail ends up better than it was rather than wider.
 *
 * 🪤 THE ITEMS COUNT IS THE ONLY HONEST TELL. `.cc-stat` blocks are positional —
 * Trades, Sales, Items, Value — and only the third means "has a collection". A
 * card can carry real trades and still have nothing on the floor, which is a
 * legitimate Top Collections omission and not a bug in his data.
 *
 * 🪤 SECTION-LEVEL REMOVAL MATCHES HIS OWN THRESHOLD, and is deliberately his
 * number rather than one we picked: `renderTopCollections` returns "" below
 * three collectors, so a rail thinned past that point is one HE would not have
 * painted. `#/collections` has no such gate and keeps whatever survives — it is
 * a full index, not a highlight rail.
 *
 * Idempotent: removed nodes cannot match on a later pass.
 */
const MIN_COLLECTOR_RAIL_CARDS = 3;

function collectorItemCount(card: HTMLElement): number | null {
  const stats = Array.from(card.querySelectorAll<HTMLElement>(".cc-stat"));
  for (const stat of stats) {
    const label = stat.querySelector(".microlabel")?.textContent?.trim().toLowerCase();
    if (label !== "items") continue;
    const raw = stat.querySelector("b")?.textContent?.trim() ?? "";
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  // No Items stat at all: his markup changed under us. Leave the card alone —
  // removing on a selector we no longer understand is the worse failure.
  return null;
}

function fixEmptyCollectorCards(doc: Document): void {
  for (const card of Array.from(doc.querySelectorAll<HTMLElement>(".collector-card"))) {
    if (collectorItemCount(card) === 0) card.remove();
  }
  const shelf = doc.getElementById("collections-shelf");
  if (!shelf) return;
  if (shelf.querySelectorAll(".collector-card").length < MIN_COLLECTOR_RAIL_CARDS) shelf.remove();
}

/** `win` is optional so the callers that only exercise pure-DOM fixups can pass
 *  a document alone; the two price-check fixups and the three wallet fixups
 *  need the hash or a bridge lookup, and are skipped without it. */
/**
 * The last sentence a seller reads before committing, and it is false.
 *
 * app.js:1785 paints, one line above the final button:
 *   "Looks right? It goes live on the floor the moment you list it."
 * The button beneath it now answers with the under-review sentence, because
 * POST /api/listings files the row as `under_review` and a verify job decides
 * later. So the wizard makes a promise and its own next frame breaks it, three
 * inches apart, both visible in one screenshot (owner, 2026-08-28).
 *
 * This was TRUE when he wrote it — his handler unshifted the row onto LISTINGS
 * and it really did appear on the floor immediately. Same expired premise as
 * the rest of this lane: the sentence outlived the behaviour it described.
 *
 * 📌 THE isAvailable BRANCH ONLY. The same element carries a second sentence
 * for collection-only drafts, "It lands in your collection - visible on your
 * profile, closed to offers.", and that one is still exactly true: POST
 * /api/collections creates the row outright and wizardPublish merges and
 * navigates on the 201. Rewriting both would replace a true sentence with a
 * vaguer one, so the match is on the false text and nothing else.
 *
 * 🪤 MATCHED ON TEXT, NOT ON `.wz-hint`. That class appears more than twenty
 * times across his file, on everything from a character counter to the
 * shipping-weight help. A structural selector here would need to encode which
 * step is rendered and would silently start rewriting a neighbour the first
 * time he reorders the wizard. Matching the exact sentence is both narrower
 * and self-limiting: it cannot hit a string it is not, and once replaced the
 * node no longer matches, which is what makes it idempotent across the
 * MutationObserver's repeated passes.
 *
 * If Blake reworks the sentence this stops firing and his new words ship
 * unedited. That is the correct failure: demoContract.test.ts pins the needle,
 * so the change reddens there and gets a decision rather than being papered
 * over by a fuzzy match.
 */
export const FLOOR_PROMISE_FALSE =
  "Looks right? It goes live on the floor the moment you list it.";

/** Owner-approved 2026-08-28. Matches the tone of the toast it sits above. */
export const FLOOR_PROMISE_TRUE =
  "Looks right? We'll review it and let you know when it's live.";

function fixWizardFloorPromise(doc: Document): void {
  for (const hint of Array.from(doc.querySelectorAll<HTMLElement>(".wz-hint"))) {
    if (hint.textContent?.trim() !== FLOOR_PROMISE_FALSE) continue;
    hint.textContent = FLOOR_PROMISE_TRUE;
  }
}

export function applyDemoTextFixups(doc: Document, win?: Window): void {
  // Before fixDanglingMemberSince: that fixup rewrites ".cc-since" text inside
  // these very cards, and there is no point repairing a label on a card this
  // pass is about to remove.
  fixEmptyCollectorCards(doc);
  fixFollowerRoster(doc);
  // Independent of the line above — fixFollowerRoster repairs an OPEN
  // modal's roster; this closes the trigger that opens it in the first
  // place, on the public profile only. Order between the two does not
  // matter (see the function's own header for why).
  neutralizePublicFollowerTriggers(doc);
  fixMarketVitals(doc);
  fixRepProgress(doc);
  fixVoteBarsNoVotes(doc);
  fixWizardFloorPromise(doc);
  fixLogoDayCacheBust(doc);
  // fixEmptyCategoryTiles MUST run before fixMarketsHeader (F2, #671 review):
  // the header's "N markets" count reads the mosaic's SURVIVING .tile count,
  // which is only accurate once the empty ones have already been removed
  // this pass.
  fixEmptyCategoryTiles(doc);
  fixMarketsHeader(doc);
  fixCategoryEmptyState(doc);
  fixCategoryBrowseCount(doc);
  fixDiscoverTryChip(doc);
  fixDanglingMemberSince(doc);
  fixUnreadBadge(doc);
  fixPriceCheckAskMarker(doc);
  if (win) fixPriceCheckAttribution(doc, win);
  if (win) {
    // ONE lookup for the three, not three: they read the same object, and a
    // per-fixup call would let two of them see different states if a fetch
    // resolved mid-pass. Null only if the bridge drifted — every other
    // "not on the wallet view" case is each fixup's own selector miss.
    const facts = walletFacts(win);
    if (facts) {
      fixFoundingMemberCredit(doc, facts);
      fixSellerPendingEscrow(doc, facts);
      fixPayoutsUnavailable(doc, facts);
    }
  }
}

/** Must run after app.js has loaded, same ordering requirement as
 *  installLinkFixups — see WebInstall.tsx's call site. */
export function installDemoTextFixups(win: Window): void {
  const doc = win.document;
  const app = doc.getElementById("app");
  if (!app) {
    console.warn("[web-install] installDemoTextFixups: #app not found, demo-text fixups not installed");
    return;
  }
  if (typeof (win as Window & { __gbzListingPriceCheck?: unknown }).__gbzListingPriceCheck !== "function") {
    console.warn(
      "[web-install] installDemoTextFixups: window.__gbzListingPriceCheck is missing — the price-check " +
        "attribution line cannot be painted, so an UNGROUNDED estimate will render with no qualifier.",
    );
  }
  applyDemoTextFixups(doc, win);
  const observer = new MutationObserver(() => {
    applyDemoTextFixups(doc, win);
  });
  observer.observe(app, { childList: true, subtree: true });
}

// Exported for tests only, same as ratingFixups.ts's NO_RATING_* constants.
export {
  NO_FOLLOWERS_COPY,
  NO_FOLLOWING_COPY,
  PC_ATTRIBUTION_CLASS,
  MOSAIC_REFLOW_CLASS,
  MEGA_REFLOW_CLASS,
  PENDING_ESCROW_CLASS,
  PAYOUTS_UNAVAILABLE_COPY,
  PENDING_PAYOUT_LABEL,
  ESCROW_SELLER_NOTE,
  MEMBER_SINCE_LABEL_RE,
};
